import { useEffect, useState, useCallback } from 'react';

import { useSelector, useDispatch } from 'react-redux';

import {
  setBrandSelectedLocalStorage,
  getBrandSelectedLocalStorage,
  removeBrandSelectedLocalStorage,
} from 'utils/menuMaker';

import { selectedBrand, getBrands } from './actions';
import { selectBrands, selectBrandsFetching, selectBrandsFetched, selectSelectedBrand } from './selectors';

function useBrands() {
  const dispatch = useDispatch();
  const [brandsList, setBrandsList] = useState([]);
  const brands = useSelector(selectBrands);
  const loading = useSelector(selectBrandsFetching);
  const isLoaded = useSelector(selectBrandsFetched);
  const brandSelected = useSelector(selectSelectedBrand);
  const brandSelectedLocalStorage = getBrandSelectedLocalStorage();

  /**
   * Validates if the stored brand is valid for the current context
   * Ensures brand consistency between store and organization modes
   *
   * @param {string} brandUuid - Brand UUID from localStorage
   * @param {Array} availableBrands - Currently available brands
   * @returns {boolean} Whether the brand is valid for current context
   */
  const validateBrandForCurrentContext = useCallback((brandUuid, availableBrands) => {
    if (!brandUuid || !availableBrands?.length) return false;

    // Check if the brand exists in the current context's available brands
    const brandExists = availableBrands.some((brand) => brand?.uuid === brandUuid);
    return brandExists;
  }, []);

  useEffect(() => {
    setBrandsList(brands);
  }, [brands]);

  useEffect(() => {
    if (isLoaded && brands?.length > 0) {
      if (brandSelectedLocalStorage) {
        // Validate if the stored brand is valid for current context
        const isValidBrand = validateBrandForCurrentContext(brandSelectedLocalStorage, brands);

        if (isValidBrand) {
          const brand = brands.find((item) => item?.uuid === brandSelectedLocalStorage);
          if (brand) {
            handleSetSelectedBrand(brand);
          }
        } else {
          // Clear invalid brand from localStorage and reset selection
          removeBrandSelectedLocalStorage();
          handleResetSelectedBrand();
        }
      } else {
        // No brand selected, reset to default (first available brand)
        handleResetSelectedBrand();
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, brands, validateBrandForCurrentContext]);

  const handleGetBrands = useCallback(() => {
    dispatch(getBrands());
  }, [dispatch]);

  const handleSetSelectedBrand = useCallback(
    (brand) => {
      setBrandSelectedLocalStorage({ brandUuid: brand?.uuid });
      dispatch(selectedBrand.selected(brand));
    },
    [dispatch],
  );

  const handleResetSelectedBrand = useCallback(() => {
    dispatch(selectedBrand.reset());
  }, [dispatch]);

  const handleBrandsReset = useCallback(() => {
    dispatch(getBrands.reset());
  }, [dispatch]);

  return {
    brands: brandsList,
    brandsState: { isLoading: loading, isLoaded },
    brandSelected,
    getBrands: handleGetBrands,
    loading,
    resetSelectedBrand: handleResetSelectedBrand,
    setSelectedBrand: handleSetSelectedBrand,
    brandsReset: handleBrandsReset,
  };
}

export default useBrands;
